import React, { useState, useEffect } from 'react';
import './Goals.css';

const Goals = () => {
  // Sample initial goals state
  const [goals, setGoals] = useState([]);
  const [newGoal, setNewGoal] = useState({
    title: '',
    description: '',
    targetDate: '',
    category: 'cardio',
    targetValue: '',
    unit: 'km'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch goals on component mount
  useEffect(() => {
    // This would be replaced with an actual API call
    const fetchGoals = async () => {
      try {
        setLoading(true);
        // Simulate API call with timeout
        setTimeout(() => {
          const sampleGoals = [
            {
              id: 1,
              title: 'Run 5K',
              description: 'Complete a 5K run without stopping',
              targetDate: '2025-05-30',
              category: 'cardio',
              targetValue: '5',
              unit: 'km',
              progress: 60
            },
            {
              id: 2,
              title: 'Increase Bench Press',
              description: 'Bench press 100kg for 5 reps',
              targetDate: '2025-06-15',
              category: 'strength',
              targetValue: '100',
              unit: 'kg',
              progress: 75
            },
            {
              id: 3,
              title: 'Weekly Yoga',
              description: 'Attend yoga class 3 times a week',
              targetDate: '2025-04-30',
              category: 'flexibility',
              targetValue: '3',
              unit: 'sessions',
              progress: 33
            }
          ];
          setGoals(sampleGoals);
          setLoading(false);
        }, 1000);
      } catch (err) {
        setError('Failed to fetch goals. Please try again later.');
        setLoading(false);
      }
    };

    fetchGoals();
  }, []);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewGoal({
      ...newGoal,
      [name]: value
    });
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Add new goal to the list
    const goal = {
      id: goals.length + 1,
      ...newGoal,
      progress: 0
    };
    
    setGoals([...goals, goal]);
    
    // Reset form
    setNewGoal({
      title: '',
      description: '',
      targetDate: '',
      category: 'cardio',
      targetValue: '',
      unit: 'km'
    });
  };

  // Update unit based on category selection
  useEffect(() => {
    switch(newGoal.category) {
      case 'cardio':
        setNewGoal(prev => ({ ...prev, unit: 'km' }));
        break;
      case 'strength':
        setNewGoal(prev => ({ ...prev, unit: 'kg' }));
        break;
      case 'flexibility':
        setNewGoal(prev => ({ ...prev, unit: 'sessions' }));
        break;
      case 'weight':
        setNewGoal(prev => ({ ...prev, unit: 'lbs' }));
        break;
      default:
        setNewGoal(prev => ({ ...prev, unit: '' }));
    }
  }, [newGoal.category]);

  // Handle goal deletion
  const handleDelete = (goalId) => {
    setGoals(goals.filter(goal => goal.id !== goalId));
  };

  // Render progress bar with appropriate color
  const renderProgressBar = (progress) => {
    let colorClass = 'progress-low';
    if (progress >= 70) {
      colorClass = 'progress-high';
    } else if (progress >= 40) {
      colorClass = 'progress-medium';
    }
    
    return (
      <div className="progress-container">
        <div 
          className={`progress-bar ${colorClass}`}
          style={{ width: `${progress}%` }}
        ></div>
        <span className="progress-text">{progress}%</span>
      </div>
    );
  };

  if (loading) {
    return <div className="goals-loading">Loading your fitness goals...</div>;
  }

  if (error) {
    return <div className="goals-error">{error}</div>;
  }

  return (
    <div className="goals-container">
      <h2>Set and Track Your Fitness Goals</h2>
      
      <div className="goals-dashboard">
        <div className="goal-metrics">
          <div className="metric-card">
            <h3>Active Goals</h3>
            <div className="metric-value">{goals.length}</div>
          </div>
          <div className="metric-card">
            <h3>In Progress</h3>
            <div className="metric-value">{goals.filter(goal => goal.progress > 0 && goal.progress < 100).length}</div>
          </div>
          <div className="metric-card">
            <h3>Achieved</h3>
            <div className="metric-value">{goals.filter(goal => goal.progress === 100).length}</div>
          </div>
        </div>

        <div className="goals-section">
          <h3>Your Fitness Goals</h3>
          {goals.length === 0 ? (
            <p className="no-goals">You haven't set any goals yet. Create your first fitness goal below!</p>
          ) : (
            <div className="goals-list">
              {goals.map(goal => (
                <div key={goal.id} className="goal-card">
                  <div className="goal-header">
                    <h4>{goal.title}</h4>
                    <span className={`goal-category ${goal.category}`}>{goal.category}</span>
                  </div>
                  <p className="goal-description">{goal.description}</p>
                  <div className="goal-details">
                    <div className="goal-target">
                      <span className="label">Target:</span> {goal.targetValue} {goal.unit}
                    </div>
                    <div className="goal-date">
                      <span className="label">By:</span> {new Date(goal.targetDate).toLocaleDateString()}
                    </div>
                  </div>
                  {renderProgressBar(goal.progress)}
                  <div className="goal-actions">
                    <button className="btn-update">Update Progress</button>
                    <button className="btn-delete" onClick={() => handleDelete(goal.id)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="goal-form-section">
          <h3>Create New Goal</h3>
          <form onSubmit={handleSubmit} className="goal-form">
            <div className="form-group">
              <label htmlFor="title">Goal Title</label>
              <input
                type="text"
                id="title"
                name="title"
                value={newGoal.title}
                onChange={handleInputChange}
                required
                placeholder="E.g., Run 5K, Weekly Yoga"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={newGoal.description}
                onChange={handleInputChange}
                placeholder="Describe your goal in detail"
              ></textarea>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="category">Category</label>
                <select
                  id="category"
                  name="category"
                  value={newGoal.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="cardio">Cardio</option>
                  <option value="strength">Strength</option>
                  <option value="flexibility">Flexibility</option>
                  <option value="weight">Weight Management</option>
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="targetDate">Target Date</label>
                <input
                  type="date"
                  id="targetDate"
                  name="targetDate"
                  value={newGoal.targetDate}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="targetValue">Target Value</label>
                <input
                  type="number"
                  id="targetValue"
                  name="targetValue"
                  value={newGoal.targetValue}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your target"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="unit">Unit</label>
                <input
                  type="text"
                  id="unit"
                  name="unit"
                  value={newGoal.unit}
                  onChange={handleInputChange}
                  readOnly
                />
              </div>
            </div>
            
            <button type="submit" className="btn-create">Create Goal</button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Goals;